// The Admin equipment record page (Design Constitution A40):
// `/admin/equipment/<slug>`, one page per piece of equipment.
//
// Like the cut-sheet editor (`cutSheetRecordPaths.ts`) this is a RECORD SPACE:
// every address under the prefix names one piece, and there is no hub beneath
// it, so the route row is a splat with no VIEW_TO_PATH entry. `/admin` itself
// stays EXACT (the webforms view), as do `/admin/client-errors` and
// `/admin/newsletter`; this prefix is strictly longer than `/admin` and shares
// no prefix with the other two, so none of the four can reach another.
//
// This module is the one place the accepted pathname shape is defined. The App
// URL→view chain, the released-surface boundary and the page itself all read
// it, so they can never disagree about a given URL.

export const ADMIN_EQUIPMENT_RECORD_PATHNAME_PREFIX = '/admin/equipment/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

/**
 * The equipment slug this pathname names, or null when the pathname is not a
 * canonical record address. Returns the DECODED segment. The page matches it
 * against the piece's slug first and its id second, the same order the Fleet
 * record page uses, so an older id-shaped link still opens its piece.
 */
export function adminEquipmentRecordSlug(pathname: unknown): string | null {
  if (typeof pathname !== 'string' || !pathname.startsWith(ADMIN_EQUIPMENT_RECORD_PATHNAME_PREFIX)) return null;
  const rawSegment = pathname.slice(ADMIN_EQUIPMENT_RECORD_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return null;
  try {
    const segment = decodeURIComponent(rawSegment);
    return isSafeDecodedSegment(segment) ? segment : null;
  } catch {
    return null;
  }
}

/** `/admin/equipment/<slug>` with exactly one decoded-safe segment. */
export function isCanonicalAdminEquipmentRecordPathname(pathname: unknown): pathname is string {
  return adminEquipmentRecordSlug(pathname) !== null;
}

/**
 * The full location form. No search and no hash are carried: which section is
 * open is page state, not part of the address.
 */
export function isCanonicalAdminEquipmentRecordLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalAdminEquipmentRecordPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;
  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '') &&
    isCanonicalAdminEquipmentRecordPathname(pathname)
  );
}

/** The record address for one piece. Encoded once, here. */
export function adminEquipmentRecordPath(slugOrId: string): string {
  return `${ADMIN_EQUIPMENT_RECORD_PATHNAME_PREFIX}${encodeURIComponent(slugOrId)}`;
}

/** Where the record page's back link returns: the Admin Equipment tab. */
export const ADMIN_EQUIPMENT_LIST_PATH = '/admin?tab=equipment';
